/**
 * Storezone — Input
 */

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import './Input.css'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string
  helperText?: string
  error?: string
  prefixEl?: ReactNode
  icon?: ReactNode
  suffixEl?: ReactNode
  fullWidth?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      prefixEl,
      icon,
      suffixEl,
      fullWidth = false,
      id,
      className = '',
      ...props
    },
    ref,
  ) => {
    const inputId = id ?? `sz-input-${Math.random().toString(36).slice(2, 9)}`
    const helperId = `${inputId}-helper`
    const errorId = `${inputId}-error`
    const startEl = prefixEl || icon

    return (
      <div className={`sz-input-root ${fullWidth ? 'sz-input-root--full' : ''} ${className}`}>
        {label && (
          <label htmlFor={inputId} className="sz-input-label">
            {label}
          </label>
        )}
        <div className={`sz-input-wrap ${error ? 'sz-input-wrap--error' : ''}`}>
          {startEl && <span className="sz-input-prefix">{startEl}</span>}
          <input
            ref={ref}
            id={inputId}
            className="sz-input"
            aria-describedby={
              error ? errorId : helperText ? helperId : undefined
            }
            aria-invalid={error ? true : undefined}
            {...props}
          />
          {suffixEl && <span className="sz-input-suffix">{suffixEl}</span>}
        </div>
        {error && (
          <p id={errorId} className="sz-input-error" role="alert">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={helperId} className="sz-input-helper">
            {helperText}
          </p>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'
